/**
 * Pair With Pouch — combined total price in the Add to Cart button.
 * (Emily's request, 2026-08-23.)
 *
 * purchase-panel-layout.js already merges the native <price-list> into the
 * Add to Cart button so the price reads inline with the button label. This
 * adds one more behavior on top of that: once the pouch add-on
 * (pair-with-pouch.js) is confirmed ("added" state — color + monogram
 * chosen, "ADD POUCH" clicked), the price shown there should reflect the
 * pen + pouch combined total, not just the pen's own price, so the number
 * next to "Add to Cart" always matches what actually lands in the cart when
 * that button is clicked. Reverts to the pen-only price automatically once
 * the pouch is added to the cart (pair-with-pouch.js resets to "collapsed")
 * or its selection is cleared.
 *
 * Timing note, matching purchase-panel-layout.js's own approach: Prestige
 * re-renders the price-list's markup asynchronously after a variant change
 * (confirmed live: <sale-price> is a brand-new DOM node after every pack
 * change, not the same node with updated text), so a plain 'variant:change'
 * listener alone can't be trusted to see the final result — the
 * MutationObserver on the same `.product-info__block-list` container is the
 * real trigger. That "always a fresh node after a native re-render" behavior
 * is also what makes the state tracking below safe: our own
 * data-pwp-total-applied marker can never survive a genuine native
 * re-render, so a node carrying it is always trustworthy as "still ours."
 *
 * NOTE 2026-08-24: wrapped in an IIFE. Prestige's pack-size switcher
 * re-injects this file's <script> tag into the DOM on every variant change
 * (confirmed live via console), and the previous top-level `const pwpPanel`
 * would collide with itself on re-injection ("Identifier 'pwpPanel' has
 * already been declared"), throwing on every pack switch. An IIFE scopes
 * each injection independently so re-injection is a harmless no-op instead
 * of a crash — the first injection's listeners (bound to `document` and to
 * the persistent `.product-info__block-list` node) keep doing the real work
 * across variant changes regardless.
 */
(function () {
  function pwpFormatMoney(cents, currencySymbol) {
    return `${currencySymbol}${(cents / 100).toFixed(2)}`;
  }

  function pwpGetPouchAddonCents() {
    const pouchEl = document.querySelector('pair-with-pouch');
    if (!pouchEl || pouchEl.state !== 'added' || !pouchEl.pendingSelection) return 0;
    return parseInt(pouchEl.dataset.pwpPriceCents || '0', 10) || 0;
  }

  function updatePairWithPouchButtonPrice() {
    const scope = document.querySelector('.shopify-section--main-product');
    const priceList = scope && scope.querySelector('price-list');
    const salePriceEl = priceList && priceList.querySelector('sale-price');
    if (!salePriceEl) return;

    const pouchAddonCents = pwpGetPouchAddonCents();
    const srOnly = salePriceEl.querySelector('.sr-only');
    const srOnlyHtml = srOnly ? srOnly.outerHTML : '<span class="sr-only">Sale price</span>';

    if (salePriceEl.dataset.pwpTotalApplied === 'true') {
      // This node already shows our combined total. If the pouch is no longer
      // pending (added to cart, or its selection was cleared before that),
      // restore the pen-only price we cached when we applied the override.
      if (pouchAddonCents <= 0) {
        const penPriceCents = parseInt(salePriceEl.dataset.pwpPenPriceCents || '', 10);
        const currency = salePriceEl.dataset.pwpCurrency || '$';
        if (!Number.isNaN(penPriceCents)) {
          salePriceEl.innerHTML = `${srOnlyHtml}${pwpFormatMoney(penPriceCents, currency)}`;
        }
        delete salePriceEl.dataset.pwpTotalApplied;
        delete salePriceEl.dataset.pwpPenPriceCents;
        delete salePriceEl.dataset.pwpCurrency;
      }
      // Otherwise it's still applied and still correct (nothing changed) — leave it.
      return;
    }

    // Fresh/native node (Prestige just (re-)rendered it) — it shows the real
    // per-variant price as-is. Only touch it if a pouch selection is pending.
    if (pouchAddonCents <= 0) return;

    const clone = salePriceEl.cloneNode(true);
    const srInClone = clone.querySelector('.sr-only');
    if (srInClone) srInClone.remove();
    const visibleText = clone.textContent.trim();

    const currencyMatch = visibleText.match(/^\D+/);
    const currency = currencyMatch ? currencyMatch[0] : '$';
    const digits = visibleText.replace(/[^0-9.]/g, '');
    const penPriceCents = Math.round(parseFloat(digits) * 100);
    if (!penPriceCents || Number.isNaN(penPriceCents)) return;

    salePriceEl.dataset.pwpPenPriceCents = String(penPriceCents);
    salePriceEl.dataset.pwpCurrency = currency;
    salePriceEl.innerHTML = `${srOnlyHtml}${pwpFormatMoney(penPriceCents + pouchAddonCents, currency)}`;
    salePriceEl.dataset.pwpTotalApplied = 'true';
  }

  document.addEventListener('DOMContentLoaded', updatePairWithPouchButtonPrice);
  document.addEventListener('variant:change', updatePairWithPouchButtonPrice);
  document.addEventListener('pair-with-pouch:state', updatePairWithPouchButtonPrice);
  document.addEventListener('cart:change', updatePairWithPouchButtonPrice);

  const pwpPanel = document.querySelector('.shopify-section--main-product .product-info__block-list');
  if (pwpPanel) {
    new MutationObserver(updatePairWithPouchButtonPrice).observe(pwpPanel, { childList: true, subtree: true });
  }
})();
